import {useState, useRef} from 'react'
import {Link, useParams} from 'react-router'
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import cn from 'classnames'
import {getTask, setTaskStatus, addTaskComment} from '../../api/tasks.js'
import {useAuth} from '../../auth/use-auth.js'
import {Button} from '../../components/button/button.jsx'
import styles from './task-detail.module.scss'

const STATUSES = [
    {value: 'pending', label: 'Pending'},
    {value: 'in_progress', label: 'In progress'},
    {value: 'blocked', label: 'Blocked'},
    {value: 'delayed', label: 'Delayed'},
    {value: 'resolved', label: 'Resolved'}
]

const PRIORITY_LABEL = (p) => p >= 4 ? 'High' : p >= 3 ? 'Medium' : 'Low'

const PRIORITY_COLOR = (p) => p >= 4 ? styles.priorityHigh : p >= 3 ? styles.priorityMid : styles.priorityLow

function formatDate(iso) {
    if (!iso)
        return '—'
    return new Date(iso).toLocaleString(undefined, {dateStyle: 'medium', timeStyle: 'short'})
}

function relativeTime(iso) {
    if (!iso)
        return ''
    const diffMs = Date.now() - new Date(iso).getTime()
    const min = Math.round(diffMs / 60_000)
    if (min < 1)
        return 'JUST NOW'
    if (min < 60)
        return `${min}M AGO`
    const hr = Math.round(min / 60)
    if (hr < 24)
        return `${hr}H AGO`
    const day = Math.round(hr / 24)
    return `${day}D AGO`
}

export function TaskDetail() {
    const {id} = useParams()
    const {user} = useAuth()
    const queryClient = useQueryClient()
    const [commentDraft, setCommentDraft] = useState('')
    const [statusError, setStatusError] = useState(null)
    const composerRef = useRef(null)

    const taskQuery = useQuery({
        queryKey: ['tasks', id],
        queryFn: () => getTask(id),
        staleTime: 10_000
    })

    const statusMutation = useMutation({
        mutationFn: (nextStatus) => setTaskStatus(id, nextStatus),
        onMutate: async (nextStatus) => {
            await queryClient.cancelQueries({queryKey: ['tasks', id]})
            const previous = queryClient.getQueryData(['tasks', id])
            if (previous?.task) {
                queryClient.setQueryData(['tasks', id], {
                    ...previous,
                    task: {...previous.task, status: nextStatus}
                })
            }
            setStatusError(null)
            return {previous}
        },
        onError: (_err, _next, context) => {
            if (context?.previous)
                queryClient.setQueryData(['tasks', id], context.previous)
            setStatusError('Couldn\'t update status. Try again.')
        },
        onSettled: () => {
            queryClient.invalidateQueries({queryKey: ['tasks', id]})
            queryClient.invalidateQueries({queryKey: ['tasks', 'me']})
        }
    })

    const commentMutation = useMutation({
        mutationFn: (body) => addTaskComment(id, body),
        onSuccess: () => {
            setCommentDraft('')
            queryClient.invalidateQueries({queryKey: ['tasks', id]})
            composerRef.current?.focus()
        }
    })

    if (taskQuery.isLoading)
        return (
            <div className={styles.screen}>
                <p className={styles.loading}>Loading task…</p>
            </div>
        )

    if (taskQuery.isError || !taskQuery.data?.task)
        return (
            <div className={styles.screen}>
                <Link to="/tasks" className={styles.back}>← Tasks</Link>
                <p className={styles.errorPanel}>Couldn't load this task. It may have been deleted.</p>
            </div>
        )

    const {task, comments = []} = taskQuery.data
    const canMutate = !!user && (user.id === task.assigneeId || user.roles?.admin === true)
    const priorityClass = PRIORITY_COLOR(task.priority)
    const priorityLabel = PRIORITY_LABEL(task.priority)

    const handleSubmitComment = (event) => {
        event.preventDefault()
        const body = commentDraft.trim()
        if (!body || commentMutation.isPending)
            return
        commentMutation.mutate(body)
    }

    return (
        <div className={styles.screen}>
            <Link to="/tasks" className={styles.back}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <path d="M19 12H5M12 19l-7-7 7-7" stroke="currentColor" strokeWidth="1.5"/>
                </svg>
                Tasks
            </Link>

            <header className={cn(styles.head, priorityClass)}>
                <p className={styles.meta}>
                    <span className={styles.priorityDot} aria-hidden="true"/>
                    <span>{priorityLabel} priority</span>
                    <span className={styles.dot}>·</span>
                    <span>Task {task.id}</span>
                </p>
                <h1 className={styles.title}>{task.title}</h1>
            </header>

            {task.description && (
                <p className={styles.description}>{task.description}</p>
            )}

            <div className={styles.metaGrid}>
                <div className={styles.metaCell}>
                    <div className={styles.metaLabel}>Assignee</div>
                    <div className={styles.metaValue}>{task.assigneeId === user?.id ? 'You' : task.assigneeId}</div>
                </div>
                <div className={styles.metaCell}>
                    <div className={styles.metaLabel}>Due</div>
                    <div className={styles.metaValue}>{formatDate(task.dueAt)}</div>
                </div>
                <div className={styles.metaCell}>
                    <div className={styles.metaLabel}>Created</div>
                    <div className={styles.metaValue}>{formatDate(task.createdAt)}</div>
                </div>
                <div className={styles.metaCell}>
                    <div className={styles.metaLabel}>Updated</div>
                    <div className={styles.metaValue}>{formatDate(task.updatedAt)}</div>
                </div>
            </div>

            <section className={styles.section}>
                <h2 className={styles.sectionTitle}>Status</h2>
                <div className={styles.statusRow} role="radiogroup" aria-label="Task status">
                    {STATUSES.map(s => (
                        <button
                            key={s.value}
                            type="button"
                            role="radio"
                            aria-checked={task.status === s.value}
                            className={cn(styles.statusBtn, {[styles.statusActive]: task.status === s.value})}
                            onClick={() => statusMutation.mutate(s.value)}
                            disabled={!canMutate || statusMutation.isPending}
                        >
                            {s.label}
                        </button>
                    ))}
                </div>
                {!canMutate && (
                    <p className={styles.gated}>Only the assignee or an admin can change status.</p>
                )}
                {statusError && (
                    <p className={styles.statusError} role="alert">{statusError}</p>
                )}
            </section>

            <section className={styles.section}>
                <h2 className={styles.sectionTitle}>Comments · {comments.length}</h2>
                {comments.length === 0 && (
                    <p className={styles.empty}>No comments yet.</p>
                )}
                <ul className={styles.commentList}>
                    {comments.map(c => (
                        <li key={c.id} className={styles.comment}>
                            <div className={styles.commentHead}>
                                <span className={styles.avatar} aria-hidden="true">
                                    {(c.authorId || '?').slice(2, 3).toUpperCase() || '?'}
                                </span>
                                <span className={styles.commentWho}>{c.authorId === user?.id ? 'You' : c.authorId}</span>
                                <span className={styles.commentWhen}>{relativeTime(c.createdAt)}</span>
                            </div>
                            <p className={styles.commentBody}>{c.body}</p>
                        </li>
                    ))}
                </ul>
            </section>

            <form className={styles.composer} onSubmit={handleSubmitComment}>
                <input
                    ref={composerRef}
                    type="text"
                    value={commentDraft}
                    onChange={(event) => setCommentDraft(event.target.value)}
                    placeholder="Add a comment…"
                    aria-label="Add a comment"
                    className={styles.composerInput}
                    disabled={commentMutation.isPending}
                />
                <Button
                    type="submit"
                    size="sm"
                    disabled={!commentDraft.trim() || commentMutation.isPending}
                    loading={commentMutation.isPending}
                >
                    Send
                </Button>
            </form>
        </div>
    )
}
